import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.72; /* Scentreat: footer carries upsell slider + protection + summary, keep it pinned */

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal) {
      // Scentreat: open the drawer the moment the add starts (even on an empty cart).
      this.#themeDrawer?.open();
    }

    // Scentreat: show the lines being added right away (title / image / price read from the
    // page) until the real cart HTML comes back - the cart mutation itself takes ~1s.
    if (event.action === 'add' && !sourceModal && (shouldAutoOpen || this.#themeDrawer?.isOpen)) {
      if (this.#isCartEmpty()) this.setAttribute('adding', '');
      this.#renderOptimistic(event);
    }

    event.promise
      ?.then(({ detail }) => {
        this.#settleOptimistic(false);
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        this.#settleOptimistic(true);
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  /**
   * Scentreat: placeholder rows for the lines being added, built from the product page DOM.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #renderOptimistic(event) {
    this.#clearOptimistic();
    const lines = /** @type {any} */ (event).lines || /** @type {any} */ (event).detail?.lines || [];
    const source = event.target instanceof Element ? event.target : null;
    const section = source?.closest('.shopify-section');
    if (!section || !lines.length) return;

    const hints = /** @type {any} */ (source)?.stLineHints || null;
    const rows = lines.map((line) => this.#optimisticRowHtml(section, line, hints)).filter(Boolean);
    if (this.#isCartEmpty()) {
      const inner = this.querySelector('.cart-drawer__inner');
      if (!inner) return;
      const block = document.createElement('div');
      block.className = 'st-optimistic cart-drawer__items' + (rows.length ? '' : ' st-optimistic--spinner');
      block.setAttribute('aria-busy', 'true');
      if (rows.length) {
        block.innerHTML =
          '<div class="cart-items"><table class="cart-items__table" role="presentation"><tbody>' +
          rows.join('') +
          '</tbody></table></div>';
      }
      inner.appendChild(block);
    } else {
      const tbody = this.querySelector('.cart-items__table tbody');
      if (!tbody) return;
      // Shopify lists the newest line first
      for (const html of rows) tbody.insertAdjacentHTML('afterbegin', html);
    }
  }

  /**
   * @param {Element} section
   * @param {{ merchandiseId: string | number, quantity: number }} line
   * @param {Record<string, { title?: string, image?: string, price?: string }> | null} [hints]
   */
  #optimisticRowHtml(section, line, hints = null) {
    // merchandiseId arrives as a GID (gid://shopify/ProductVariant/123): compare the numeric part
    const id = String(line.merchandiseId).split('/').pop() || '';
    const qty = Number(line.quantity) || 1;
    let title = '';
    let image = '';
    let price = '';
    // Scentreat (21/09/2026): callers without a product form (the oil bundle picker) put what to show on
    // the element that fires the event, as stLineHints[variantId] = { title, image, price }.
    const hint = hints?.[id];
    if (hint?.title) {
      title = String(hint.title);
      image = String(hint.image || '');
      price = String(hint.price || '');
    }
    const form = title ? undefined : [...section.querySelectorAll('product-form-component')].find((f) => {
      const input = f.querySelector('input[name="id"]');
      return input instanceof HTMLInputElement && input.value === id;
    });
    const upsell = form?.closest('st-upsell-component');
    if (upsell) {
      // offer titles read like "FREE Lavender Essential Oil — $19.95 Value": keep the product part
      title = (upsell.querySelector('.st-upsell__title')?.textContent?.trim() || '').replace(/\s+[—–]\s.*$/, '');
      image = this.#optimisticImage(upsell.querySelector('img'));
    } else if (form && section.querySelector('media-gallery')) {
      title = section.querySelector('h1')?.textContent?.trim() || '';
      image = this.#optimisticImage(section.querySelector('media-gallery img'));
      const priceEl = section.querySelector('.price-item__group.price, product-price .price, .price');
      const matches = priceEl?.textContent?.match(/[^\d\s]{0,3}\s?\d[\d.,]*/g);
      price = matches ? matches[matches.length - 1].trim() : '';
    }
    if (!title) return '';

    /** @param {string} s */
    const esc = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);
    const media = image
      ? '<div class="cart-items__media-container" style="--ratio:1;"><img class="cart-items__media-image border-style" src="' +
        esc(image) +
        '" alt="" width="250" height="250"></div>'
      : '';
    return (
      '<tr class="cart-items__table-row cart-items__table-row--full-width-variants st-optimistic-row" role="row" aria-busy="true">' +
      '<td class="cart-items__media" role="cell">' +
      media +
      '</td>' +
      '<td class="cart-items__details cart-primary-typography" role="cell"><div class="cart-items__product-info"><div class="cart-items__title-row"><span class="cart-items__title">' +
      esc(title) +
      '</span></div></div><div class="cart-items__variants-wrapper"></div></td>' +
      '<td class="cart-items__quantity" role="cell"><span class="cart-secondary-typography">' +
      (qty > 1 ? '× ' + qty : '') +
      '</span></td>' +
      '<td class="cart-items__price cart-secondary-typography" role="cell">' +
      esc(price) +
      '</td>' +
      '</tr>'
    );
  }

  /** @param {Element | null} img */
  #optimisticImage(img) {
    if (!(img instanceof HTMLImageElement)) return '';
    let src = img.currentSrc || img.src || '';
    if (!src && img.srcset) src = img.srcset.split(',')[0].trim().split(' ')[0];
    return src.replace(/([?&])width=\d+/, '$1width=250');
  }

  #clearOptimistic() {
    this.querySelectorAll('.st-optimistic, .st-optimistic-row').forEach((node) => node.remove());
  }

  /** @param {boolean} immediate */
  #settleOptimistic(immediate) {
    const done = () => {
      this.removeAttribute('adding');
      this.#clearOptimistic();
    };
    if (immediate) return done();
    // cart-items-component (same promise) morphs the real rows in; on an empty drawer that happens
    // inside a view transition, so wait until the empty state is gone (1.5s cap) before cleaning up
    const started = Date.now();
    const tick = () => {
      if (!this.#isCartEmpty() || Date.now() - started > 1500) done();
      else setTimeout(tick, 40);
    };
    setTimeout(tick, 0);
  }

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
